import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet"; // ✅ import Helmet
import "./HeroSection.css";
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    name: "Krisara Gastro and Liver Clinic",
    url: "https://krisaragastro.com/",
    logo: "https://i.ibb.co/HDcnBZt3/Rectangle-76.png",
    image: "https://i.ibb.co/HDcnBZt3/Rectangle-76.png",
    telephone: "+91 7569541532",
    email: "gastro@krisara.com",
    address: {
      "@type": "PostalAddress",
      streetAddress:
        "3rd Floor, Fairmount Square, Pet Basheerabad, Quthbullapur, Kompally",
      addressLocality: "Hyderabad",
      addressRegion: "Telangana",
      postalCode: "500014",
      addressCountry: "IN",
    },
    sameAs: [
      "https://www.facebook.com/krisaragastro",
      "https://www.instagram.com/krisaragastro",
    ],
  };
const symptomVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.6, ease: "easeOut" },
  }),
};
const leftSymptoms = [
  { text: "BLOATING AND GAS", link: "/services" },
  { text: "UPPER ABDOMINAL BURNING/PAIN", link: "/services" },
  { text: "NAUSEA OR VOMITING", link: "/services" },
  { text: "HEARTBURN/ACID REFLUX", link: "/services" },
];
const rightSymptoms = [
  { text: "UNEXPLAINED WEIGHT LOSS", link: "/services" },
  { text: "IBS: CRAMPS, DIARRHEA/CONSTIPATION", link: "/services" },
  { text: "PERSISTENT CONSTIPATION", link: "/services" },
  { text: "CHRONIC/RECURRENT DIARRHEA", link: "/services" },
];
const HeroSection = () => {
  return (
    <section className="hero">
      {/* SEO META TAGS & SCHEMA */}
      <Helmet>
        {/* Basic SEO */}
        <title>Krisara Gastro | Expert Gastroenterology Services</title>
        <meta
          name="description"
          content="Krisara Gastro offers expert gastroenterology services personalized for your digestive health. Book consultations today."
        />
        <meta name="keywords" content="Gastroenterology, Digestive Health, Bloating, Heartburn, IBS, Nausea, Gastro Clinic India" />
        <meta name="author" content="Krisara Gastro" />
        <meta name="robots" content="index, follow" />
        {/* Open Graph / Facebook */}
        <meta property="og:title" content="Krisara Gastro | Expert Gastroenterology Services" />
        <meta property="og:description" content="Personalized gastro care for your digestive health. Expert treatments and consultations." />
        <meta property="og:image" content="https://i.ibb.co/S4175JQq/hero-section-image-4-copy-1.png" />
        <meta property="og:url" content="https://krisara-gastro.web.app/" />
        <meta property="og:type" content="website" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Krisara Gastro | Expert Gastroenterology Services" />
        <meta name="twitter:description" content="Personalized gastro care for your digestive health. Expert treatments and consultations." />
        <meta name="twitter:image" content="https://i.ibb.co/S4175JQq/hero-section-image-4-copy-1.png" />
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalClinic",
              "name": "Krisara Gastro",
              "image": "https://i.ibb.co/S4175JQq/hero-section-image-4-copy-1.png",
              "url": "https://krisara-gastro.web.app/",
              "telephone": "+91 7569541532",
              "description": "Expert gastroenterology services personalized for your digestive health.",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Kompally",
                "addressLocality": "Hyderabad",
                "addressRegion": "Kompally, Hyderabad",
                "postalCode": "500014",
                "addressCountry": "IN"
              },
              "sameAs": [
                "https://www.facebook.com/yourpage",
                "https://www.instagram.com/yourpage",
                "https://www.linkedin.com/company/yourpage"
              ]
            }
          `}
        </script>
      </Helmet>
      {/* Hero Heading */}
      <motion.div
        className="hero-heading"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
        <h1>
          Expert gastroenterology <br />
          <span>Personalised</span> for You
        </h1>
      </motion.div>
      {/* Hero Image & Symptoms */}
      <div className="hero-center">
        <motion.div
          className="hero-image-wrap"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1, ease: "easeOut" }}
        >
          <motion.img
            src="https://i.ibb.co/S4175JQq/hero-section-image-4-copy-1.png"
            alt="Hero Illustration - Best Gastroenterologist in Kompally Hyderabad - Krisara Gastro Clinic"
            className="hero-image"
            animate={{ y: [0, -12, 0] }}
            transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
          />
          {leftSymptoms.map((item, i) => (
            <motion.div
              key={i}
              className={`symptom left s${i + 1}`}
              variants={symptomVariants}
              initial="hidden"
              animate="visible"
              custom={i}
            >
              <Link to={item.link} className="symptom-link">
                {item.text}
              </Link>
            </motion.div>
          ))}
          {rightSymptoms.map((item, i) => (
            <motion.div
              key={i}
              className={`symptom right s${i + 5}`}
              variants={symptomVariants}
              initial="hidden"
              animate="visible"
              custom={i + 4}>
              <Link to={item.link} className="symptom-link">
                {item.text}
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
      {/* Buttons */}
      <motion.div
        className="hero-buttons"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.8 }}>
        <Link to="/services">
          <motion.button className="learn-btn" whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }}>
            Learn More ↗
          </motion.button>
        </Link>
        <a href="tel:+919876543210">
          <motion.button className="contact-outline" whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }}>
            Contact us ↗
          </motion.button>
        </a>
      </motion.div>
    </section>
  );
};
export default HeroSection;